<template>
	<div>
		<CRow>
			<CCol md="12">
				<h2 class="typo-h4 position-relative">
					Payment condition
				</h2>
			</CCol>
		</CRow>
		<CRow>
			<CCol md="12">
				<div class="d-block mt-4 mb-3">
					<label class="mb-0">
						<h6 data-test-id="title">
							Payment method
						</h6>
					</label>
					<CButton
						type="button"
						class="btn btn-add float-right"
						color="secondary"
						data-test-id="button-add"
						@click="$refs['modal-payment-method'].open()"
					>
						{{ `${localSelectedNestedList.length ? 'Edit' : 'Add'} payment method` }}
					</CButton>
				</div>
			</CCol>
		</CRow>
		<CRow>
			<CCol v-if="isLoading" class="mb-3" lg="12">
				<BaseNoItemBanner
					text="Loading..."
				/>
			</CCol>
			<CCol v-else-if="!localSelectedNestedList.length" class="mb-3" lg="12">
				<BaseNoItemBanner
					text="There is no payment method added."
				/>
			</CCol>
			<CCol v-else lg="12">
				<div class="payment-method-container">
					<div
						v-for="group in localSelectedNestedList"
						:key="group.id"
						class="payment-group mt-3"
					>
						<span class="group-name font-weight-bold typo-body-2 mb-2 d-inline-block">
							{{ group.name }}
						</span>
						<ul v-if="group.methods && group.methods.length" class="child-list mb-1">
							<li
								v-for="child in group.methods"
								:key="child.id"
								class="list-item typo-body-2"
							>
								{{ child.name }}
							</li>
						</ul>
					</div>
				</div>
			</CCol>
		</CRow>

		<ModalPaymentMethod
			ref="modal-payment-method"
			:list="localSelectedNestedList"
			@onConfirm="handlePaymentMethodListChange"
		/>
	</div>
</template>


<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import ModalPaymentMethod from '@/components/ModalPaymentMethod.vue';

export default {
	name: 'PromotionPaymentCondition',
	components: {
		ModalPaymentMethod,
	},
	props: {
		// Flat array of selected Group IDs/Objects from API
		paymentMethods: {
			type: Array,
			default: () => [],
		},
		// Flat array of selected Method IDs/Objects from API
		childPaymentMethods: {
			type: Array,
			default: () => [],
		},
	},
	computed: {
		...mapState('payments', {
			isLoading: (state) => state.list.isLoading,
		}),
		...mapGetters({
			paymentMethodGroupList: 'payments/paymentMethodGroupList',
		}),
		localSelectedNestedList() {
			if (!this.paymentMethodGroupList.length) return [];

			const selectedGroupIds = this.paymentMethods.map((g) => g.id);
			const selectedChildIds = this.childPaymentMethods.map((c) => c.id);

			return this.paymentMethodGroupList
				.map((group) => {
					const isGroupSelected = selectedGroupIds.includes(group.id);
					const selectedMethods = (group.methods || []).filter((m) => selectedChildIds.includes(m.id));

					if (!isGroupSelected && selectedMethods.length === 0) return null;

					// If group is selected as a whole (Legacy/Full selection) but specific methods aren't listed in flat array,
					// use all methods from the master list for display purposes.
					let displayMethods = selectedMethods;
					if (isGroupSelected && selectedMethods.length === 0 && group.methods && group.methods.length > 0) {
						displayMethods = group.methods;
					}

					return {
						...group,
						methods: displayMethods,
					};
				})
				.filter(Boolean);
		},
	},
	async created() {
		// ถ้า groups ถูก load มาแล้วและมี methods อยู่ข้างใน ไม่ต้อง fetch ใหม่
		const hasMethodsLoaded = this.paymentMethodGroupList.some((g) => g.methods?.length > 0);
		if (!hasMethodsLoaded) {
			await this.getPaymentMethodGroups({ with_payment_methods: 1 });
		}
	},
	methods: {
		...mapActions({
			getPaymentMethodGroups: 'payments/getPaymentMethodGroups',
		}),
		handlePaymentMethodListChange(nestedList) {
			// A group is added to flatGroups if:
			// 1. It has no methods (standalone selection)
			// 2. All of its methods are selected
			const flatGroups = nestedList
				.filter((g) => {
					const masterGroup = this.paymentMethodGroupList.find((mg) => mg.id === g.id);
					const masterMethodCount = (masterGroup?.methods || []).length;
					return g.methods.length === masterMethodCount;
				})
				.map((g) => ({ id: g.id, name: g.name }));

			// All selected nested methods go to flatMethods
			const flatMethods = nestedList
				.reduce((acc, g) => [...acc, ...(g.methods || [])], [])
				.map((m) => ({ id: m.id, name: m.name }));

			this.$emit('update:paymentMethods', flatGroups);
			this.$emit('update:childPaymentMethods', flatMethods);
		},
	},
};
</script>

<style lang="scss" scoped>
	.payment-method-container {
		overflow-y: auto;
		min-height: rem(60);
		max-height: rem(468);
		padding: rem(8) rem(16) rem(20);
		border-radius: rem(4);
		border: solid 1px $color-gray-400;

		.payment-group {
			&:first-child {
				margin-top: 0 !important;
			}
		}

		.child-list {
			list-style: none;
			padding-left: 0;
			margin: 0;

			.list-item {
				border-radius: rem(4);
				background-color: $color-gray-300;
				color: $color-black-90;
				display: inline-block;
				padding: 0 rem(6);
				margin-right: rem(12);
				margin-bottom: rem(8);
			}
		}
	}
</style>